import { Link } from "react-router-dom";
import { ArrowRight, Quotes } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import TrustStrip from "@/components/TrustStrip";
import { WhoWeAre, WhatWeTeach, HowWeOperate } from "@/components/AboutSections";

const VALUES = [
  { code: "01", title: "Discipline", desc: "Structured curriculum, punctual classes, and no shortcuts around safety protocols." },
  { code: "02", title: "Integrity", desc: "Honest evaluations, transparent pricing, and instruction grounded in state law — never marketing spin." },
  { code: "03", title: "Capability", desc: "Real training built by an active-duty law-enforcement instructor team — sworn officers who work the profession, not classroom-only trainers." },
  { code: "04", title: "Adaptability", desc: "Every student is different. We adjust pace, coaching, and drills to make sure everyone leaves ready." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About — State-Certified Firearms & Security Training Academy"
        description="Established 2024 in Maryland. Secrecy Executive Training Academy delivers state-certified firearms and security training led by an active-duty law-enforcement instructor team — founder S. Williams (multi-state licensed) and Derek Kinard (active Maryland police officer)."
        path="/about"
      />
      <section className="relative border-b border-white/10 pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="/media/range-a-landscape.jpg" alt="Live-fire coaching at the SEP range" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        </div>
        <div className="container-x relative">
          <div className="overline mb-4">// FILE 002 — ORIGIN · EST. 2024</div>
          <h1 className="font-heading text-5xl md:text-8xl tracking-tighter leading-[0.9] max-w-5xl">
            Built to train <span className="text-[#FFD300]">the standard we set.</span>
          </h1>
        </div>
      </section>

      <WhoWeAre />

      <section className="section-pad border-t border-white/10">
        <div className="container-x grid grid-cols-12 gap-12">
          <div className="col-span-12 md:col-span-5">
            <div className="overline mb-4">// THE MISSION</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tighter leading-tight">Our reason for existing.</h2>
          </div>
          <div className="col-span-12 md:col-span-7 text-lg text-neutral-300 leading-relaxed space-y-6">
            <p>
              <em className="text-[#FFD300] not-italic">To deliver state-certified firearms and security training that empowers civilians and professionals to carry, guard, and protect with confidence and legality.</em>
            </p>
            <p className="text-base text-neutral-400">
              Founded in 2024 and headquartered in Calverton, Maryland, Secrecy Executive is a training academy
              offering permit courses across seven states, professional certifications for security officers
              and protective agents, and life-saving skills like CPR and first aid. Every course is instructor-led,
              scenario-based, and built to meet the exact requirements each state expects for its permit or license.
            </p>
          </div>
        </div>
      </section>

      <TrustStrip />

      <WhatWeTeach />

      <HowWeOperate />

      <section className="bg-black border-b border-white/10 section-pad">
        <div className="container-x">
          <div className="overline mb-4">// 005 — CORE VALUES</div>
          <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-none mb-16">Four non-negotiables.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {VALUES.map((v) => (
              <div key={v.code} className="bg-black p-10" data-testid={`value-${v.code}`}>
                <div className="font-mono text-[11px] tracking-[0.2em] text-[#FFD300] mb-6">// {v.code}</div>
                <h3 className="font-heading text-3xl tracking-tighter mb-4">{v.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-12 gap-12 items-center">
          <div className="col-span-12 md:col-span-5">
            <Quotes size={48} weight="duotone" className="text-[#FFD300] mb-6" />
            <blockquote className="font-heading text-3xl md:text-4xl tracking-tighter leading-tight mb-6">
              "We don't teach to a script. We teach so that when the moment comes, our students know exactly what to do — legally, safely, and confidently."
            </blockquote>
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-400">— S. Williams, Founder &amp; Lead Instructor</div>
          </div>
          <div className="col-span-12 md:col-span-7">
            <div className="aspect-[4/3] overflow-hidden">
              <img src="/media/range-coaching.jpg" alt="Instructor S. Williams coaching a student through live-fire technique" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FFD300] text-black" data-testid="about-cta">
        <div className="container-x py-16 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-8">
            <div className="font-mono text-xs tracking-[0.25em] uppercase mb-2">// JOIN THE STANDARD</div>
            <h3 className="font-heading text-3xl md:text-5xl tracking-tighter leading-none">Ready to enroll?</h3>
          </div>
          <div className="col-span-12 md:col-span-4 flex flex-wrap gap-3 md:justify-end">
            <Link to="/training" className="bg-black text-white font-mono uppercase tracking-wider text-sm py-4 px-6 hover:bg-neutral-900">Browse Courses <ArrowRight size={14} className="inline" /></Link>
            <Link to="/contact" className="bg-white text-black font-mono uppercase tracking-wider text-sm py-4 px-6 hover:bg-neutral-100">Contact <ArrowRight size={14} className="inline" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
